/** Win line highlight / dim effects for winning symbol positions */

import { Container, Text, TextStyle } from 'pixi.js';
import type { WinResult } from '../core/SpinResultGenerator';
import type { ReelSet } from '../reels/ReelSet';
import { GameConfig } from '../config/GameConfig';
import { ReelSymbol } from '../reels/ReelSymbol';
import { AudioManager } from '../audio/AudioManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('WinLineAnimation');

/** Manages win presentation animations: dimming non-winners, highlighting winners */
export class WinLineAnimation {
  private _isPresenting: boolean = false;
  private _cancelled: boolean = false;
  private _reelSet: ReelSet | null = null;
  private _winAmountText: Text | null = null;
  private _winTextContainer: Container | null = null;

  get isPresenting(): boolean {
    return this._isPresenting;
  }

  /** Bind to a ReelSet for accessing individual reel symbols */
  setReelSet(reelSet: ReelSet): void {
    this._reelSet = reelSet;
  }

  /** Set the container and text where per-win amounts are displayed */
  setWinDisplay(container: Container, text: Text): void {
    this._winTextContainer = container;
    this._winAmountText = text;
  }

  /**
   * Present wins by cycling through each winning combination.
   * For each win:
   *   1. Dim all non-winning symbols (alpha to 0.3)
   *   2. Highlight winning symbol positions
   *   3. Show per-win amount text
   *   4. Dwell for configurable time
   *   5. Undim and move to next win
   */
  async presentWins(wins: WinResult[], totalBet: number): Promise<void> {
    if (wins.length === 0) return;
    if (!this._reelSet) {
      logger.warn('No ReelSet bound — cannot present wins');
      return;
    }

    this._isPresenting = true;
    this._cancelled = false;

    logger.info(`Presenting ${wins.length} win(s)`);

    const betPerWay = totalBet / 243;

    for (let i = 0; i < wins.length; i++) {
      if (this._cancelled) break;

      const win = wins[i];
      const winAmount = win.symbol === 'SCATTER'
        ? win.coins * totalBet
        : win.coins * betPerWay;

      logger.debug(
        `Showing win ${i + 1}/${wins.length}: ${win.symbol} x${win.streakLength} ` +
        `(${win.ways} ways) = $${winAmount.toFixed(4)}`
      );

      // Build set of winning positions for quick lookup
      const winPositionSet = new Set<string>();
      for (const [reel, row] of win.positions) {
        winPositionSet.add(`${reel},${row}`);
      }

      // Dim all symbols, then highlight winners
      this._dimAll();
      await this._highlightPositions(winPositionSet);
      AudioManager.getInstance().playSFX('sfx_win_single');

      // Show per-win amount
      if (this._winAmountText) {
        this._winAmountText.text =
          `${win.symbol} x${win.streakLength} (${win.ways} ways) = $${winAmount.toFixed(2)}`;
      }

      // Dwell
      await this._delay(GameConfig.WIN_DWELL_TIME);

      if (this._cancelled) break;

      // Undim all
      this._undimAll();
    }

    // Clear win text after cycling
    if (this._winAmountText && !this._cancelled) {
      this._winAmountText.text = '';
    }

    this._undimAll();
    this._isPresenting = false;
  }

  /** Skip/cancel the current win presentation */
  skip(): void {
    this._cancelled = true;
    this._undimAll();
    if (this._winAmountText) {
      this._winAmountText.text = '';
    }
    this._isPresenting = false;
    logger.debug('Win presentation skipped');
  }

  /** Dim all visible symbols to low alpha */
  private _dimAll(): void {
    if (!this._reelSet) return;
    for (const reel of this._reelSet.reels) {
      // Access the symbol container's children (ReelSymbol instances)
      // The reel's children[0] is the symbol container
      const symbolContainer = reel.children[0] as Container;
      if (!symbolContainer) continue;
      for (const child of symbolContainer.children) {
        child.alpha = 0.3;
      }
    }
  }

  /** Restore all visible symbols to full alpha */
  private _undimAll(): void {
    if (!this._reelSet) return;
    for (const reel of this._reelSet.reels) {
      const symbolContainer = reel.children[0] as Container;
      if (!symbolContainer) continue;
      for (const child of symbolContainer.children) {
        child.alpha = 1.0;
        if (child instanceof ReelSymbol) {
          child.stopWinAnimation();
        }
      }
    }
  }

  /**
   * Highlight specific reel/row positions by restoring their alpha.
   * Positions are stored as "reel,row" strings in the set.
   */
  private async _highlightPositions(positionSet: Set<string>): Promise<void> {
    if (!this._reelSet) return;
    const bufferSymbols = GameConfig.BUFFER_SYMBOLS;
    const animPromises: Promise<void>[] = [];

    for (const reel of this._reelSet.reels) {
      const reelIdx = this._reelSet.reels.indexOf(reel);
      const symbolContainer = reel.children[0] as Container;
      if (!symbolContainer) continue;

      for (let row = 0; row < GameConfig.VISIBLE_ROWS; row++) {
        const key = `${reelIdx},${row}`;
        if (positionSet.has(key)) {
          // The visible symbols in the container start at index = bufferSymbols
          const childIdx = row + bufferSymbols;
          if (childIdx < symbolContainer.children.length) {
            const symbol = symbolContainer.children[childIdx];
            symbol.alpha = 1.0;
            if (symbol instanceof ReelSymbol) {
              animPromises.push(symbol.playWinAnimation());
            }
          }
        }
      }
    }

    // Fire-and-forget: kick off all win animations in parallel
    Promise.all(animPromises).catch(() => {});
  }

  private _delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      // Allow cancellation to resolve immediately
      const checkCancel = setInterval(() => {
        if (this._cancelled) {
          clearTimeout(timer);
          clearInterval(checkCancel);
          resolve();
        }
      }, 50);
      // Clean up interval when timer fires
      setTimeout(() => clearInterval(checkCancel), ms + 100);
    });
  }
}
